<script lang="ts">
    import type { CustomField } from '../../api/formSettings';

    export let fields: CustomField[] = [];
    export let customFieldValues: Record<number, string> = {};
    export let touched: Record<string, boolean> = {};
    export let errors: Record<string, string> = {};
    export let onFieldBlur: (field: CustomField) => void = () => {};
    export let showRequired: (mandatory: boolean) => boolean = (mandatory) => mandatory;

    function getAnswerType(answerType: string | undefined): string {
        return (answerType || '').toLowerCase();
    }

    function isSingleChoiceField(answerType: string | undefined): boolean {
        const normalized = getAnswerType(answerType);
        return normalized.includes('radio')
            || normalized.includes('singleanswer')
            || normalized.includes('single_choice')
            || normalized.includes('singlechoice');
    }

    function isMultiChoiceField(answerType: string | undefined): boolean {
        const normalized = getAnswerType(answerType);
        return normalized.includes('multipleanswers')
            || normalized.includes('multianswer')
            || normalized.includes('multi_choice')
            || normalized.includes('multiplechoice')
            || normalized.includes('checkboxlist');
    }
</script>

{#each fields as field}
    <div class="space-y-1.5">
        {#if !(field.answerType === 'Checkbox' || field.answerType === 'Boolean')}
            <label for="custom_{field.id}" class="block text-base font-medium text-gray-700">
                {field.showInstructionsAsDisplayName && field.instructions ? field.instructions : field.displayName}
                {#if showRequired(field.isMandatoryWhenDisplayed)}<span class="text-red-500">*</span>{/if}
            </label>
        {/if}

        {#if field.answerType === 'Text'}
            <input
                id="custom_{field.id}"
                type="text"
                bind:value={customFieldValues[field.id]}
                on:blur={() => onFieldBlur(field)}
                class="w-full px-4 py-3 border rounded-lg transition-all duration-200 outline-none
                    {touched[`custom_${field.id}`] && errors[`custom_${field.id}`]
                        ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                        : 'border-gray-300 input-brand-focus'}"
            />
        {:else if field.answerType === 'Checkbox' || field.answerType === 'Boolean'}
            <label for="custom_{field.id}" class="custom-option-label inline-flex items-center gap-2 text-base text-gray-700 cursor-pointer">
                <input
                    id="custom_{field.id}"
                    type="checkbox"
                    checked={customFieldValues[field.id] === 'True'}
                    on:change={(e) => customFieldValues[field.id] = e.currentTarget.checked ? 'True' : 'False'}
                    class="custom-option-control w-5 h-5 checkbox-brand border-gray-300 rounded"
                />
                <span class="custom-option-text">
                    {field.showInstructionsAsDisplayName && field.instructions ? field.instructions : field.displayName}
                    {#if showRequired(field.isMandatoryWhenDisplayed)}<span class="text-red-500">*</span>{/if}
                </span>
            </label>
        {:else if field.answerType === 'Radio'}
            <div class="custom-option-group flex items-center gap-6">
                <label class="custom-option-label flex items-center gap-2.5 text-[0.9375rem] leading-6 text-gray-700 cursor-pointer">
                    <input
                        type="radio"
                        name={"custom_" + field.id}
                        value="True"
                        checked={customFieldValues[field.id] === 'True' || (!customFieldValues[field.id] && field.autoYesIfBoolean)}
                        on:change={() => customFieldValues[field.id] = 'True'}
                        class="custom-option-control w-4 h-4 radio-brand border-gray-300 cursor-pointer"
                    />
                    <span class="custom-option-text">Yes</span>
                </label>
                <label class="custom-option-label flex items-center gap-2.5 text-[0.9375rem] leading-6 text-gray-700 cursor-pointer">
                    <input
                        type="radio"
                        name={"custom_" + field.id}
                        value="False"
                        checked={customFieldValues[field.id] === 'False'}
                        on:change={() => customFieldValues[field.id] = 'False'}
                        class="custom-option-control w-4 h-4 radio-brand border-gray-300 cursor-pointer"
                    />
                    <span class="custom-option-text">No</span>
                </label>
            </div>
        {:else if field.answerType === 'MultipleOptionsSingleAnswer'}
            <select
                id="custom_{field.id}"
                bind:value={customFieldValues[field.id]}
                on:blur={() => onFieldBlur(field)}
                class="w-full px-3 py-3 border rounded-lg transition-all duration-200 outline-none bg-white
                    {touched[`custom_${field.id}`] && errors[`custom_${field.id}`]
                        ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                        : 'border-gray-300 input-brand-focus'}"
            >
                <option value="">-- Please select --</option>
                {#each field.possibleAnswers || [] as answer}
                    <option value={answer}>{answer}</option>
                {/each}
            </select>
        {:else if field.answerType === 'Datepicker'}
            <input
                id="custom_{field.id}"
                type="date"
                bind:value={customFieldValues[field.id]}
                on:blur={() => onFieldBlur(field)}
                class="w-full px-4 py-3 border rounded-lg transition-all duration-200 outline-none
                    {touched[`custom_${field.id}`] && errors[`custom_${field.id}`]
                        ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                        : 'border-gray-300 input-brand-focus'}"
            />
        {:else if isSingleChoiceField(field.answerType) && (field.possibleAnswers?.length || 0) > 0}
            <div class="custom-option-stack space-y-2">
                {#each field.possibleAnswers || [] as answer}
                    <label class="custom-option-label flex items-center gap-2.5 text-[0.9375rem] leading-6 text-gray-700 cursor-pointer">
                        <input
                            type="radio"
                            name={"custom_" + field.id}
                            value={answer}
                            checked={customFieldValues[field.id] === answer}
                            on:change={() => customFieldValues[field.id] = answer}
                            class="custom-option-control w-4 h-4 radio-brand border-gray-300 cursor-pointer"
                        />
                        <span class="custom-option-text">{answer}</span>
                    </label>
                {/each}
            </div>
        {:else if isMultiChoiceField(field.answerType) && (field.possibleAnswers?.length || 0) > 0}
            <div class="custom-option-stack space-y-2">
                {#each field.possibleAnswers || [] as answer}
                    <label class="custom-option-label flex items-center gap-2 text-base text-gray-700 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={(customFieldValues[field.id] || '').split('|').includes(answer)}
                            on:change={(e) => {
                                const values = (customFieldValues[field.id] || '').split('|').filter(v => v);
                                const next = e.currentTarget.checked
                                    ? [...values.filter(v => v !== answer), answer]
                                    : values.filter(v => v !== answer);
                                customFieldValues[field.id] = next.join('|');
                            }}
                            class="custom-option-control w-4 h-4 checkbox-brand border-gray-300 rounded cursor-pointer"
                        />
                        <span class="custom-option-text">{answer}</span>
                    </label>
                {/each}
            </div>
        {/if}

        {#if !field.showInstructionsAsDisplayName && field.instructions}
            <p class="text-sm text-gray-500">{@html field.instructions}</p>
        {/if}

        {#if touched[`custom_${field.id}`] && errors[`custom_${field.id}`]}
            <p class="text-sm text-red-500">{errors[`custom_${field.id}`]}</p>
        {/if}
    </div>
{/each}

<style>
    .custom-option-group,
    .custom-option-stack,
    .custom-option-label {
        align-items: center !important;
    }

    .custom-option-label {
        display: inline-flex !important;
        vertical-align: middle;
    }

    .custom-option-control {
        flex: 0 0 auto;
        margin: 0 !important;
        align-self: center !important;
        vertical-align: middle;
    }

    .custom-option-text {
        display: inline-block;
        margin: 0 !important;
        padding: 0 !important;
        line-height: 1.5rem !important;
        vertical-align: middle;
    }
</style>
